/**
 * The signed-in shell: a **light** 236px rail and a page beside it, with a
 * 60px header on top of the page.
 *
 * The header carries the page's name and the identity, and **nothing else**.
 * It used to hold each page's own verbs, portalled up from the page through a
 * slot, and that is the one thing it no longer does: a verb that makes
 * something — a workspace, a team, an invitation, a shared row — belongs beside
 * the list it adds to, where whoever is reading the list can see what it will
 * do. A button in the chrome is a button in a different room, and the page
 * underneath had to be read twice to work out which of its lists it meant.
 *
 * The rail is light on purpose. It is the one place where this area would
 * otherwise stop looking like the rest of the product: a dark sidebar is what
 * makes a web surface read as an *administration console*, and what a person
 * does here is configure the same app they already use.
 *
 * Three rules hold everywhere in this file, and the chrome is what enforces
 * them:
 *
 * - **A filled shape is ink, never teal.** `Invite`, `Publish`, `New
 *   workspace`, a switch that is on — all black. Teal marks where you *are*
 *   (ink plus weight), a tint, an outline, a state dot. It never fills.
 * - **No counters in the chrome.** Not on a rail row, not on a tab, not in a
 *   title's meta. The only numbers in the product are the content of the
 *   overview — which is the difference between a decorative total and an
 *   answer.
 * - **A selection is not a slab.** Where you are is said by ink and by weight
 *   600. That is what dropped some thirty totals and every pill out of the
 *   navigation at once.
 */
import { useEffect, useRef } from 'react';
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { AirlockLockup } from './AirlockMark';
import { ChevronDown, LockMark } from './icons';
import { initialsOf } from '../lib/format';
import { useTooltip } from './Tooltip';
import { PAGES, ROW_OF, type PageId, type RailGroup } from './nav';
import type { User } from '../lib/types';

/** 236px, and it never disappears — not on a page, not inside a workspace. */
export const RAIL_WIDTH = 236;

/**
 * Closing a menu the three ways a person expects: a click anywhere else, the
 * Escape key, and choosing something. The click listener is capture-phase, so
 * a click on something that stops propagation still closes it.
 */
export function useDismiss(open: boolean, onClose: () => void): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('click', away, true);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('click', away, true);
      document.removeEventListener('keydown', key);
    };
  }, [open, onClose]);
  return ref;
}

/* ── the breadcrumb ────────────────────────────────────────────────────────
 *
 * The mark, then what is open. Not a slab and not a box — and it wraps rather
 * than truncating a name, because the one thing it exists to say is which
 * organization you are looking at.
 */

export interface CrumbPick {
  key: string;
  label: string;
  current?: boolean;
  onPick: () => void;
}

export interface CrumbTrail {
  /** The segment before this one, which is a place you can go back to. */
  parent?: { label: string; onGo: () => void };
  /** Where you are. Bold, ink, and what the chevron changes. */
  here: string;
  /**
   * That `here` is the product rather than a place, so the wordmark is drawn
   * instead of the name — the same lockup the sign-in page opens with.
   *
   * A personal account is the one shell with nothing to name: no organization,
   * no workspace. `here` is still carried, because the chevron's label has to
   * say what it is switching away from.
   */
  wordmark?: boolean;
  /** Whether what you are in is private to whoever owns it. */
  locked?: boolean;
  /** What the chevron opens. With none, there is no chevron. */
  picks?: CrumbPick[];
  /** The verb under the rule at the foot of the picker. */
  action?: { label: string; onPick: () => void };
}

function Crumb({
  trail,
  open,
  onToggle,
  onClose
}: {
  trail: CrumbTrail;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const ref = useDismiss(open, onClose);
  // A chevron promises somewhere else to go. An account with one organization
  // and nothing else to switch to opened a menu holding the name it was drawn
  // under — the place said twice, and a click that does nothing. The list is
  // still the whole list once there is a second entry, current one included,
  // because that is what says which of them you are in.
  const pickable = Boolean(trail.picks?.some((pick) => !pick.current) || trail.action);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      {/* In the header since the redesign (§38.2): the mark heads the rail. */}
      {trail.wordmark && <span style={{ fontSize: 13.5, fontWeight: 500, color: color.ink }}>Airlock</span>}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '2px 7px'
        }}
      >
        {trail.parent && (
          <>
            <button
              type="button"
              className="link-quiet"
              onClick={trail.parent.onGo}
              style={{
                flex: 'none',
                background: 'none',
                border: 0,
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13.5,
                color: color.inkSubtle
              }}
            >
              {trail.parent.label}
            </button>
            <span aria-hidden="true" style={{ flex: 'none', fontSize: 13.5, color: color.inkGhost }}>
              /
            </span>
          </>
        )}
        {/* The padlock belongs to the name, so the two wrap as one: on a long
            name the trail breaks after the slash, never between the lock and
            what it is locking. */}
        {!trail.wordmark && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            {trail.locked && (
              <span style={{ flex: 'none', display: 'flex', color: color.ink }} title="Private to this organization">
                <LockMark />
              </span>
            )}
            <span aria-current="page" style={{ fontSize: 13.5, fontWeight: 500, color: color.ink, overflowWrap: 'anywhere' }}>
              {trail.here}
            </span>
          </span>
        )}
        {pickable && (
          <button
            type="button"
            onClick={onToggle}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={`Switch from ${trail.here}`}
            style={{
              flex: 'none',
              display: 'flex',
              background: 'none',
              border: 0,
              padding: '0 0 0 1px',
              cursor: 'pointer',
              color: color.inkSubtle
            }}
          >
            <ChevronDown open={open} />
          </button>
        )}
      </span>

      {open && pickable && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 30,
            left: 10,
            zIndex: 30,
            width: RAIL_WIDTH - 24,
            display: 'flex',
            flexDirection: 'column',
            padding: 5,
            border: `1px solid ${color.line}`,
            borderRadius: 10,
            background: color.surface,
            boxShadow: '0 1px 2px rgba(22,25,31,.06), 0 18px 40px -14px rgba(22,25,31,.22)'
          }}
        >
          {trail.picks?.map((pick) => (
            <button
              key={pick.key}
              role="menuitem"
              type="button"
              className="me-item"
              onClick={() => {
                onClose();
                pick.onPick();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                height: 30,
                padding: '0 10px',
                borderRadius: 7,
                border: 0,
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: pick.current ? 600 : 400,
                color: pick.current ? color.ink : color.inkBody
              }}
            >
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pick.label}
              </span>
            </button>
          ))}
          {trail.action && (
            <>
              {Boolean(trail.picks?.length) && <span style={{ height: 1, margin: '3px 4px', background: color.hairSoft }} />}
              <button
                role="menuitem"
                type="button"
                className="me-item"
                onClick={() => {
                  onClose();
                  trail.action?.onPick();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 30,
                  padding: '0 10px',
                  borderRadius: 7,
                  border: 0,
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  color: color.inkSubtle
                }}
              >
                {trail.action.label}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── the rail ──────────────────────────────────────────────────────────── */

/**
 * A section that contains other places opens **under itself** rather than
 * pushing a second rail onto the page, and one is open at a time.
 *
 * Its children line up on the parent's *label* — 37 = 10 of padding + 17 of
 * icon + 10 of gap — and not on its icon. That is what makes them read as the
 * section's contents rather than as rows of the same rank.
 */
const KID_INDENT = 37;

export function Rail({
  groups,
  page,
  section,
  landing,
  onGo,
  onClose,
  head
}: {
  groups: RailGroup[];
  page: PageId;
  section: string | null;
  /** Where the mark leads: the shell's own landing. */
  landing: PageId;
  onGo: (page: PageId, section?: string | null) => void;
  /** Set when the rail is the drawer under 1024 (§38.3): it carries « Close menu ». */
  onClose?: () => void;
  /** What the drawer stacks under the mark: the breadcrumb's selectors (§38.3). */
  head?: React.ReactNode;
}): React.JSX.Element {
  const lit = ROW_OF[page] ?? page;

  return (
    <aside
      className={onClose ? 'nsb' : 'nsb al-rail'}
      style={{
        flex: 'none',
        width: RAIL_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
        padding: '18px 10px',
        background: color.surface,
        boxShadow: `inset -1px 0 0 ${color.line}`,
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto'
      }}
    >
      {/* The rail's head is the mark, 52 like the header beside it (§38.1). */}
      <div style={{ height: 34, display: 'flex', alignItems: 'center', padding: '0 10px' }}>
        <button
          type="button"
          onClick={() => onGo(landing)}
          aria-label="Overview"
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: color.ink, display: 'flex' }}
        >
          <AirlockLockup size={24} labelSize={12} />
        </button>
        {onClose && (
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 0,
              borderRadius: radius.chip,
              background: 'transparent',
              color: color.inkMuted,
              cursor: 'pointer'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        )}
      </div>
      {head}

      {groups.map((group, index) => (
        <nav key={group.label ?? `group-${index}`} aria-label={group.label ?? 'Navigation'}>
          {group.label && (
            <div
              className="al-rail-label"
              style={{
                padding: '0 10px 5px',
                fontFamily: font.mono,
                fontSize: 10.5,
                fontWeight: 400,
                letterSpacing: '.16em',
                textTransform: 'uppercase',
                color: color.inkFaint,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {group.label}
            </div>
          )}
          <div className="al-rail-rows" style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {group.rows.map((row) => {
              const here = row.page === lit;
              // A section is expanded when you are inside it, and only then:
              // a rail that remembers four open sections is the wall this
              // navigation was drawn to replace.
              const expanded = Boolean(row.children) && here;
              return (
                <div key={row.page}>
                  <button
                    type="button"
                    className="rail-row"
                    // A row that opens a section is not a destination, so it
                    // does not claim to be the current page — the child it
                    // opens on does.
                    aria-current={here && !row.children ? 'page' : undefined}
                    aria-expanded={row.children ? expanded : undefined}
                    // The first child *this row is showing*, not the first the
                    // catalogue has: a section the rail leaves out — an
                    // integration this organization has not turned on — would
                    // otherwise be where clicking the section itself lands.
                    onClick={() => onGo(row.page, row.children ? (expanded ? section : row.children[0]?.key ?? null) : null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      height: 30,
                      padding: '0 10px',
                      border: 0,
                      borderRadius: radius.md,
                      background: here && !row.children ? 'rgba(0,0,0,.055)' : 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 13.5,
                      fontWeight: here ? 500 : 400,
                      color: here ? color.ink : color.inkBody
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.label}
                    </span>
                    {row.children && (
                      <span style={{ flex: 'none', display: 'flex', color: color.inkGhost }}>
                        <ChevronDown open={expanded} />
                      </span>
                    )}
                  </button>

                  {expanded && row.children && (
                    <div className="al-rail-kids" style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '1px 0 5px' }}>
                      {row.children.map((kid) => {
                        const on = kid.key === section;
                        return (
                          <button
                            key={kid.key}
                            type="button"
                            className="rail-kid"
                            aria-current={on ? 'page' : undefined}
                            onClick={() => onGo(row.page, kid.key)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              width: '100%',
                              minHeight: 28,
                              padding: `0 10px 0 ${KID_INDENT}px`,
                              border: 0,
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              fontSize: 13,
                              fontWeight: on ? 600 : 400,
                              color: on ? color.ink : color.inkSubtle
                            }}
                          >
                            {kid.dot && (
                              <span
                                aria-hidden="true"
                                style={{
                                  flex: 'none',
                                  width: 5,
                                  height: 5,
                                  borderRadius: radius.chip,
                                  background: kid.dot === 'on' ? color.accent : color.inkGhost
                                }}
                              />
                            )}
                            <span
                              style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {kid.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      ))}
      <span style={{ flex: 1 }} />
    </aside>
  );
}

/* ── the header ────────────────────────────────────────────────────────── */

/**
 * A framed verb. The one that commits carries the ink fill — and it is the
 * only fill on the row, which is what makes it findable without a colour.
 */
export function HeaderButton({
  children,
  onClick,
  primary,
  href,
  icon,
  disabled,
  reason,
  pressed
}: {
  children: React.ReactNode;
  onClick?: () => void;
  primary?: boolean;
  /** A display toggle's state (§40.9): its label says what it will do, aria-pressed what it does now. */
  pressed?: boolean;
  href?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Why a held button is held (rule 6): it stays focusable and says so. */
  reason?: string;
}): React.JSX.Element {
  const held = Boolean(reason);
  const { trigger, tip } = useTooltip<HTMLButtonElement>(reason);
  const style: React.CSSProperties = {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    height: 32,
    padding: primary ? '0 13px' : '0 12px',
    border: `1px solid ${primary ? 'transparent' : color.line}`,
    borderRadius: radius.chip,
    background: primary ? color.ink : 'transparent',
    color: primary ? '#FFFFFF' : color.inkBody,
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: primary ? 500 : 400,
    textDecoration: 'none',
    cursor: disabled || held ? 'default' : 'pointer',
    opacity: disabled || held ? 0.4 : 1
  };
  const className = primary ? 'hdr-btn-primary' : 'hdr-btn';
  return href ? (
    <a className={className} href={href} onClick={onClick} data-press="" style={style}>
      {icon}
      {children}
    </a>
  ) : (
    <>
      <button
        className={className}
        type="button"
        onClick={held ? undefined : onClick}
        disabled={disabled}
        aria-disabled={held || undefined}
        aria-pressed={pressed}
        data-press=""
        {...trigger}
        style={style}
      >
        {icon}
        {children}
      </button>
      {tip}
    </>
  );
}

export function PageHeader({
  title,
  trail,
  crumbOpen,
  onCrumbToggle,
  onCrumbClose,
  user,
  menu,
  menuOpen,
  onToggleMenu,
  onCloseMenu,
  onGo,
  onSystem,
  onSignOut,
  onDrawer
}: {
  title: string;
  trail: CrumbTrail;
  crumbOpen: boolean;
  onCrumbToggle: () => void;
  onCrumbClose: () => void;
  user: User;
  /** The pages this shell's identity menu holds, in the order it lists them. */
  menu: readonly PageId[];
  menuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onGo: (page: PageId) => void;
  /** The way into the deployment's own pages, for whoever has one and is not
   *  already there. Absent is how a menu says there is nowhere to go. */
  onSystem?: () => void;
  onSignOut: () => void;
  /** Opens the rail as a drawer; the button shows only under 1024 (§38.3). */
  onDrawer?: () => void;
}): React.JSX.Element {
  return (
    <>
    <header
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        height: 52,
        padding: '0 var(--al-gutter)',
        background: color.surface,
        boxShadow: `inset 0 -1px 0 ${color.line}`,
        position: 'sticky',
        top: 0,
        zIndex: 20
      }}
    >
      {/* The breadcrumb names the place; the page's own title opens the
          content under the header (redesign §38.2). */}
      {onDrawer && (
        <button
          type="button"
          className="al-menu-button"
          aria-label="Menu"
          onClick={onDrawer}
          style={{
            display: 'none',
            flex: 'none',
            width: 32,
            height: 32,
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: -8,
            border: 0,
            borderRadius: radius.chip,
            background: 'transparent',
            color: color.ink,
            cursor: 'pointer'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
      )}
      <nav aria-label="Breadcrumb" style={{ minWidth: 0 }}>
        <Crumb trail={trail} open={crumbOpen} onToggle={onCrumbToggle} onClose={onCrumbClose} />
      </nav>
      <span style={{ flex: 1 }} />
      <IdentityMenu
        user={user}
        menu={menu}
        open={menuOpen}
        onToggle={onToggleMenu}
        onClose={onCloseMenu}
        onGo={onGo}
        onSystem={onSystem}
        onSignOut={onSignOut}
      />
    </header>
    <h1
      className="al-page-title"
      style={{ margin: 0, padding: '28px var(--al-gutter) 0', fontSize: 30, lineHeight: 1.15, fontWeight: 700, letterSpacing: '-.022em', color: color.ink }}
    >
      {title}
    </h1>
    </>
  );
}

/**
 * The identity, top right, and the last element of every header.
 *
 * What is mine is under my own name — which is what lets the rail list only
 * what belongs to the organization. The `Account` group left the rail when
 * this arrived: its three pages are what this menu holds, and listing them in
 * both places gave one page two doors.
 */
function IdentityMenu({
  user,
  menu,
  open,
  onToggle,
  onClose,
  onGo,
  onSystem,
  onSignOut
}: {
  user: User;
  menu: readonly PageId[];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onGo: (page: PageId) => void;
  onSystem?: () => void;
  onSignOut: () => void;
}): React.JSX.Element {
  const ref = useDismiss(open, onClose);

  const item: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    height: 30,
    padding: '0 10px',
    borderRadius: 7,
    border: 0,
    background: 'none',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    color: color.inkBody
  };
  const rule = <span style={{ height: 1, margin: '3px 4px', background: color.hairSoft }} />;

  return (
    <div ref={ref} style={{ position: 'relative', flex: 'none', display: 'flex' }}>
      <button
        type="button"
        className="me-pill"
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${user.name ?? user.email} — my account`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 32,
          padding: '0 9px 0 4px',
          border: `1px solid ${color.line}`,
          borderRadius: radius.chip,
          background: 'transparent',
          cursor: 'pointer'
        }}
      >
        <span
          style={{
            flex: 'none',
            width: 24,
            height: 24,
            borderRadius: radius.chip,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: color.ink,
            color: '#FFFFFF',
            fontFamily: font.mono,
            fontSize: 9.5,
            fontWeight: 600
          }}
        >
          {initialsOf(user.email, user.name)}
        </span>
        <span style={{ display: 'flex', color: color.inkFaint }}>
          <ChevronDown open={open} />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 38,
            right: 0,
            zIndex: 40,
            display: 'flex',
            flexDirection: 'column',
            width: 236,
            padding: 5,
            border: `1px solid ${color.line}`,
            borderRadius: 10,
            background: color.surface,
            boxShadow: '0 1px 2px rgba(22,25,31,.06), 0 18px 40px -14px rgba(22,25,31,.22)'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '7px 10px 9px' }}>
            {user.name && <span style={{ fontSize: 13, fontWeight: 600, color: color.ink }}>{user.name}</span>}
            <span
              style={{
                fontFamily: font.mono,
                fontSize: 11,
                color: color.inkFaint,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user.email}
            </span>
          </div>
          {rule}
          {/* Whatever this shell holds — the deployment's own has no Machines,
              because it has no licence for machines to be installed against. */}
          {menu.map((id) => (
            <button key={id} role="menuitem" type="button" className="me-item" style={item} onClick={() => onGo(id)}>
              {PAGES[id].title}
            </button>
          ))}
          {/* The deployment's own pages are a place, not a page: they get their
              own shell, and this is its only door — drawn where that place is
              somewhere else, which is to say from an organization. */}
          {onSystem && (
            <>
              {rule}
              <button role="menuitem" type="button" className="me-item" style={item} onClick={onSystem}>
                System administration
              </button>
            </>
          )}
          {rule}
          <button
            role="menuitem"
            type="button"
            className="me-item-out"
            style={{ ...item, color: color.badText }}
            onClick={onSignOut}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
